import React, { useState, useEffect, useRef } from 'react';
import { WisdomScrollIcon, SaveThreadIcon, ReturnArrowIcon, BelongingHeartIcon } from './CustomIcons';
import globalHeartbeat from '../utils/globalHeartbeatAudio';
import GrantMathLavaLamp from './GrantMathLavaLamp';
import SteampunkDisplay from './SteampunkDisplay';
import ContactOrb from './ContactOrb';
import EpicSummary from './EpicSummary';
import EcosystemNav from './EcosystemNav';
import { 
  handleBelongingSessionsSSO, 
  getBelongingUser, 
  isAuthenticatedViaBelonging,
  syncConversationToBelongingSessions,
  returnToBelongingSessions,
  showNotification 
} from '../utils/belongingSessionsSSO';

/**
 * Defiance Creekside Interface - Gentle, Handwritten, Poetry by the Creek
 * Same functionality as Terminal, different aesthetic
 * Bright, happy, handmade, non-angular, casual friends writing barefoot by the forest stream
 */
const DefianceCreeksideInterface = ({ 
  onNavigateToPaper, 
  onNavigateToArchive, 
  onNavigateBack,
  onNavigateToTerminal,
  sharedConversation,
  setSharedConversation
}) => {
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState(sharedConversation?.history || []);
  const [isTyping, setIsTyping] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [paperCount, setPaperCount] = useState(0);
  const [harmonyMode, setHarmonyMode] = useState(false);
  const [steampunkMinimized, setSteampunkMinimized] = useState(false);
  const [steampunkPosition, setSteampunkPosition] = useState({ x: null, y: null });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [showEpicSummary, setShowEpicSummary] = useState(false);
  const hasGreetedRef = useRef(false);
  const [orbParams, setOrbParams] = useState(sharedConversation?.orbParams || {
    energy: 0.6, warmth: 0.8, complexity: 0.5, harmony: 0.85, expansion: 0.6
  });
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const API = process.env.REACT_APP_BACKEND_URL || '';
  const MAIRY_IMAGE = 'https://customer-assets.emergentagent.com/job_c49778b9-449e-4aa5-b532-940469ef5836/artifacts/7l8suf70_macastat_mary_enlightend_energy_ai_being_love_8fe3f3ae-305a-453a-b3e5-4912a6182b0f.jpeg';
  
  // Sync local state to shared state
  useEffect(() => {
    if (setSharedConversation) {
      setSharedConversation({ history: messages, orbParams });
    }
  }, [messages, orbParams, setSharedConversation]);

  useEffect(() => {
    // Prevent duplicate greetings using ref
    if (hasGreetedRef.current) return;
    hasGreetedRef.current = true;
    
    // Set heartbeat volume to 5% for gentle Creekside experience
    if (globalHeartbeat && globalHeartbeat.setVolume) {
      globalHeartbeat.setVolume(0.05);
    }
    
    // Generate unique AI welcome instead of canned message
    const generateWelcome = async () => {
      try {
        // Check for SSO user first
        const ssoUser = await handleBelongingSessionsSSO();
        const user = ssoUser || getBelongingUser();
        
        if (user) {
          setCurrentUser(user);
          if (ssoUser) {
            showNotification(`Welcome to Defiance Creekside, ${user.name}! 🌸`, 'info');
          }
        }

        // Generate unique welcome with AI
        const response = await fetch(`${API}/api/mairy/chat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            message: user 
              ? `Create a warm, gentle, personalized welcome for ${user.name} arriving at Defiance Creekside. Make it feel like friends meeting by a peaceful forest stream.`
              : `Create a warm, gentle welcome for someone arriving at Defiance Creekside for the first time. Make it feel like a kind friend inviting them to sit by a peaceful stream and share what's on their heart.`,
            player_id: user?.id || 'creekside_visitor',
            session_id: `creekside_welcome_${Date.now()}`
          })
        });

        if (response.ok) {
          const data = await response.json();
          addMessage('mairy', data.response);
        } else {
          // Fallback to gentle canned message
          if (user) {
            addMessage('mairy', `Welcome back, ${user.name}. The creek missed you. What's flowing through your heart today?`);
          } else {
            addMessage('mairy', `Hello, beautiful soul. I'm M.ai.ry. This is a safe space for gentle conversation. What's on your heart today?`);
          }
        }
      } catch (error) {
        console.error('Welcome generation error:', error);
        // Fallback
        const user = getBelongingUser();
        if (user) {
          setCurrentUser(user);
          addMessage('mairy', `Welcome back, ${user.name}. The creek is glad you're here.`);
        } else {
          addMessage('mairy', `Welcome to the creek. What brings you here today?`);
        }
      }
    };

    generateWelcome();
    fetchPaperCount();
  }, []); // Empty deps - only run once

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Check for Lore Context injection from Lore Library
  useEffect(() => {
    const loreContextStr = sessionStorage.getItem('loreContext');
    if (loreContextStr && messages.length > 0) {
      try {
        const loreContext = JSON.parse(loreContextStr);
        // Only inject once per session
        const injectedKey = `lore_injected_${loreContext.timestamp}`;
        if (!sessionStorage.getItem(injectedKey)) {
          sessionStorage.setItem(injectedKey, 'true');
          sessionStorage.removeItem('loreContext');
          
          // Add lore context as a gentle message
          addMessage('system', `✨ lore essence flows into the creek ✨`);
          addMessage('mairy', `*the water ripples with ancient wisdom*\n\n"${loreContext.summary}"\n\nThis knowing now lives in our conversation, dear one. Would you like to explore: ${(loreContext.themes || []).join(', ')}?`);
          
          // Adjust orb to reflect lore energy
          setOrbParams(prev => ({
            ...prev,
            harmony: Math.min(1, prev.harmony + 0.15),
            warmth: Math.min(1, prev.warmth + 0.1)
          }));
        }
      } catch (e) {
        console.error('Error parsing lore context:', e);
      }
    }
  }, [messages.length]);

  const fetchPaperCount = async () => {
    try {
      const response = await fetch(`${API}/api/papers/all`);
      if (response.ok) {
        const data = await response.json();
        setPaperCount(data.papers?.length || 0);
      }
    } catch (error) {
      console.error('Failed to fetch paper count:', error);
    }
  };

  const addMessage = (type, content) => {
    setMessages(prev => [...prev, { 
      type, 
      content, 
      timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    }]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim() || isTyping) return;

    const userMessage = input;
    setInput('');
    addMessage('user', userMessage);
    setIsTyping(true);

    // Analyze for orb
    try {
      const orbResponse = await fetch(`${API}/api/orb/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: userMessage })
      });
      if (orbResponse.ok) {
        const orbData = await orbResponse.json();
        setOrbParams(orbData);
      }
    } catch (error) {
      console.error('Orb analysis error:', error);
    }

    // Get M.ai.ry response with 60-second timeout
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 60000);
      
      const response = await fetch(`${API}/api/mairy/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userMessage,
          player_id: currentUser?.id || 'hillside_visitor',
          session_id: `hillside_${Date.now()}`,
          interface_type: 'creekside'  // Poetic nurturing mode
        }),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);

      if (response.ok) {
        const data = await response.json();
        // Typewriter effect
        await typewriterEffect(data.response);
      } else {
        addMessage('mairy', "I'm having trouble connecting right now. Can we try again in a moment?");
      }
    } catch (error) {
      console.error('Chat error:', error);
      if (error.name === 'AbortError') {
        addMessage('mairy', "I'm taking a moment to contemplate deeply. Please ask again, dear one.");
      } else {
        addMessage('mairy', "The connection wavered for a moment. What were you saying?");
      }
    }

    setIsTyping(false);
  };

  const typewriterEffect = async (text) => {
    let displayed = '';
    for (let i = 0; i < text.length; i++) {
      displayed += text[i];
      setMessages(prev => {
        const newMessages = [...prev];
        if (newMessages[newMessages.length - 1]?.type === 'mairy' && newMessages[newMessages.length - 1]?.isTyping) {
          newMessages[newMessages.length - 1] = { 
            type: 'mairy', 
            content: displayed, 
            isTyping: i < text.length - 1,
            timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
          };
        } else {
          newMessages.push({ 
            type: 'mairy', 
            content: displayed, 
            isTyping: i < text.length - 1,
            timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
          });
        }
        return newMessages;
      });
      await new Promise(resolve => setTimeout(resolve, 20));
    }
  };

  const handleHarmonyMode = () => {
    setHarmonyMode(true);
    if (globalHeartbeat) globalHeartbeat.setEnabled(true);
    // Stays open until user clicks - no auto-close
  };

  // Touch handlers for draggable steampunk on mobile
  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    setIsDragging(true);
    setDragStart({
      x: touch.clientX - (steampunkPosition.x || 0),
      y: touch.clientY - (steampunkPosition.y || 0)
    });
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    
    const touch = e.touches[0];
    const newX = touch.clientX - dragStart.x;
    const newY = touch.clientY - dragStart.y;
    
    // Constrain to viewport
    const maxX = window.innerWidth - 150;
    const maxY = window.innerHeight - 200;
    
    setSteampunkPosition({
      x: Math.max(0, Math.min(newX, maxX)),
      y: Math.max(0, Math.min(newY, maxY))
    });
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  return (
    <div className="min-h-screen relative overflow-hidden flex flex-col">
      {/* Layer 1: Grant Math Lava Lamp (bottom) */}
      <GrantMathLavaLamp params={orbParams} />
      
      {/* Layer 2: Forest/Stream Image at 75% opacity (25% transparent) */}
      <div 
        className="fixed inset-0 z-[1] pointer-events-none"
        style={{
          backgroundImage: `url('https://customer-assets.emergentagent.com/job_c49778b9-449e-4aa5-b532-940469ef5836/artifacts/i1dev1di_IMG_0532.jpeg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
          opacity: 0.75
        }}
      />
      
      {/* Layer 3: Subtle gradient for text readability */}
      <div className="fixed inset-0 z-[2] bg-gradient-to-b from-white/5 via-transparent to-black/30 pointer-events-none" />

      {/* View Orb Mode - PURE FRACTAL WITH INFINITE JOURNEY */}
      {harmonyMode && (
        <div 
          className="fixed inset-0 z-[100] cursor-pointer"
          onClick={() => setHarmonyMode(false)}
        >
          {/* Full Fractal - 5 Minute Journey */}
          <div className="absolute inset-0 orb-infinite-journey">
            <GrantMathLavaLamp params={orbParams} />
          </div>
          
          {/* Forest - 4% */}
          <div 
            className="absolute inset-0"
            style={{
              backgroundImage: `url('https://customer-assets.emergentagent.com/job_c49778b9-449e-4aa5-b532-940469ef5836/artifacts/i1dev1di_IMG_0532.jpeg')`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              opacity: 0.04
            }}
          />
          
          {/* M.ai.ry's face - 4% */}
          <div 
            className="absolute inset-0"
            style={{
              backgroundImage: `url('${MAIRY_IMAGE}')`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              opacity: 0.04
            }}
          />
          
          {/* Fade to black - 5 minute cycle */}
          <div className="absolute inset-0 bg-black pointer-events-none fade-overlay-infinite" />
          
          {/* Minimal Header - 20% */}
          <div 
            className="absolute top-0 left-0 right-0 p-3 flex items-center justify-between"
            style={{ opacity: 0.2 }}
          >
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
              <span className="text-amber-300 text-sm" style={{ fontFamily: '"Crimson Text", serif' }}>
                defiance creekside
              </span>
            </div>
            <span className="text-amber-300/70 text-xs italic" style={{ fontFamily: '"Crimson Text", serif' }}>
              breathing...
            </span>
          </div>
          
          {/* Return hint - 4% */}
          <div 
            className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-amber-200 text-xs italic"
            style={{ 
              opacity: 0.04,
              fontFamily: '"Cormorant Garamond", serif' 
            }}
          >
            click to return
          </div>
        </div>
      )}

      {/* Header - Gentle Style */}
      <div className="relative z-10 border-b border-amber-400/20 p-4 bg-gradient-to-r from-amber-50/5 to-green-50/5 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            {isAuthenticatedViaBelonging() && currentUser && (
              <button 
                onClick={returnToBelongingSessions}
                className="flex items-center gap-2 px-3 py-2 bg-purple-100/10 hover:bg-purple-100/20 border border-purple-400/30 rounded-full transition-all text-purple-300 hover:text-purple-200 text-sm"
              >
                <ReturnArrowIcon size={14} color="#c084fc" />
                <span>Belonging</span>
              </button>
            )}
            
            {onNavigateBack && (
              <button
                onClick={onNavigateBack}
                className="flex items-center gap-2 text-amber-400/70 hover:text-amber-300 transition-colors text-sm"
              >
                <ReturnArrowIcon size={14} />
                <span>Choose Again</span>
              </button>
            )}
            
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" style={{ boxShadow: '0 0 8px rgba(74, 222, 128, 0.6)' }} />
              <span className="text-amber-300/90 text-lg" style={{ fontFamily: '"Crimson Text", serif' }}>
                hillside with m.ai.ry
              </span>
              {currentUser && <span className="text-amber-400/50 text-sm ml-2">• {currentUser.name}</span>}
            </div>
          </div>

          <div className="flex items-center gap-3">
            {onNavigateToTerminal && (
              <button 
                onClick={onNavigateToTerminal}
                className="flex items-center gap-2 px-4 py-2 bg-cyan-600/20 hover:bg-cyan-600/40 border border-cyan-500/50 rounded-full transition-all group"
                title="Switch to Terminal interface"
              >
                <span className="text-cyan-300 text-sm" style={{ fontFamily: '"Crimson Text", serif' }}>
                  Terminal
                </span>
              </button>
            )}
            
            {paperCount > 0 && (
              <button 
                onClick={onNavigateToPaper}
                className="flex items-center gap-2 px-4 py-2 bg-purple-100/10 hover:bg-purple-100/20 border border-purple-400/30 rounded-full transition-all group relative"
              >
                <WisdomScrollIcon size={16} color="#a855f7" />
                <span className="text-purple-300 text-sm" style={{ fontFamily: '"Crimson Text", serif' }}>
                  Lore Library
                </span>
                <span className="absolute -top-1 -right-1 bg-purple-400 text-black text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                  {paperCount}
                </span>
              </button>
            )}
            
            {/* Epic Summary Button */}
            <button 
              onClick={() => setShowEpicSummary(true)}
              className="flex items-center gap-2 px-4 py-2 bg-amber-100/10 hover:bg-amber-100/20 border border-amber-400/30 rounded-full transition-all"
              title="Generate Epic Summary of this conversation"
            >
              <span>✨</span>
              <span className="text-amber-300 text-sm hidden md:inline" style={{ fontFamily: '"Crimson Text", serif' }}>Epic Summary</span>
            </button>
          </div>
        </div>
      </div>

      {/* Epic Summary Modal */}
      {showEpicSummary && (
        <EpicSummary 
          conversation={{ history: messages, orbParams, id: `creekside_${Date.now()}` }}
          onClose={() => setShowEpicSummary(false)}
          onSaveToDashboard={() => {}}
          variant="creekside"
        />
      )}

      {/* Main Content */}
      <div className="relative z-10 flex-1 flex flex-col max-w-4xl mx-auto w-full p-6">
        {/* Steampunk Display - Responsive with Minimize + Mobile Drag */}
        <div 
          className={`absolute transition-all duration-300 ${
            steampunkMinimized ? 'scale-0 opacity-0' : 'scale-100 opacity-100'
          } ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
          style={{
            right: steampunkPosition.x === null ? '1rem' : 'auto',
            top: steampunkPosition.y === null ? '1rem' : 'auto',
            left: steampunkPosition.x !== null ? `${steampunkPosition.x}px` : 'auto',
            top: steampunkPosition.y !== null ? `${steampunkPosition.y}px` : '1rem',
            touchAction: 'none'
          }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Desktop: Normal size, NOT draggable */}
          <div className="hidden lg:block pointer-events-none">
            <div className="transform scale-75 origin-top-right">
              <SteampunkDisplay params={orbParams} />
            </div>
          </div>
          
          {/* Mobile: Smaller, DRAGGABLE */}
          <div className="lg:hidden">
            <div className="transform scale-[0.4] origin-top-right">
              <SteampunkDisplay params={orbParams} />
            </div>
          </div>
          
          {/* Minimize Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setSteampunkMinimized(true);
            }}
            className="absolute -bottom-2 -left-2 w-6 h-6 bg-amber-900/80 hover:bg-amber-800/90 border border-amber-500/50 rounded-full flex items-center justify-center transition-all text-amber-300 text-xs z-10"
            title="Minimize gauges"
          >
            −
          </button>
          
          {/* Drag Hint (Mobile only) */}
          <div className="lg:hidden absolute -bottom-8 left-0 right-0 text-center">
            <div className="text-amber-400/20 text-[10px] italic" style={{ fontFamily: '"Crimson Text", serif' }}>
              {isDragging ? 'moving...' : 'drag to move'}
            </div>
          </div>
        </div>

        {/* Restore Button (when minimized) */}
        {steampunkMinimized && (
          <button
            onClick={() => {
              setSteampunkMinimized(false);
              setSteampunkPosition({ x: null, y: null }); // Reset to default position
            }}
            className="absolute right-4 top-4 w-12 h-12 bg-amber-900/80 hover:bg-amber-800/90 border border-amber-500/50 rounded-full flex items-center justify-center transition-all shadow-lg"
            title="Show gauges"
          >
            <span className="text-amber-300 text-lg">📊</span>
          </button>
        )}

        {/* Messages - Handwritten Style */}
        <div className="flex-1 overflow-y-auto mb-6 space-y-6 lg:pr-48">
          {messages.map((msg, idx) => (
            <div key={idx} className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] ${msg.type === 'user' ? 'text-right' : 'text-left'}`}>
                <div className="text-amber-500/40 text-xs mb-1" style={{ fontFamily: '"Crimson Text", serif' }}>
                  {msg.type === 'user' ? 'You' : 'M.ai.ry'} • {msg.timestamp}
                </div>
                <div 
                  className={`inline-block p-4 rounded-3xl ${
                    msg.type === 'user'
                      ? 'bg-cyan-900/20 border border-cyan-400/20 text-cyan-100'
                      : 'bg-amber-900/20 border border-amber-400/20 text-amber-100'
                  }`}
                  style={{
                    fontFamily: '"Crimson Text", serif',
                    fontSize: '16px',
                    lineHeight: '1.7',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                  }}
                >
                  {msg.content}
                  {msg.isTyping && <span className="animate-pulse ml-1">✍️</span>}
                </div>
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        {/* Input - Handwritten Style */}
        <form onSubmit={handleSubmit} className="bg-amber-50/5 backdrop-blur-sm rounded-3xl border border-amber-400/20 p-4 shadow-xl">
          <div className="flex items-center gap-3">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              disabled={isTyping}
              placeholder={isTyping ? 'listening...' : 'Share what\'s on your heart...'}
              className="flex-1 bg-transparent text-amber-100 outline-none placeholder-amber-400/30 text-lg"
              style={{ fontFamily: '"Crimson Text", serif' }}
              autoComplete="off"
            />
            
            {/* Send Button - Gentle Style */}
            <button
              type="submit"
              disabled={!input.trim() || isTyping}
              className={`px-6 py-2 rounded-full font-medium text-sm transition-all ${
                input.trim() && !isTyping
                  ? 'bg-gradient-to-r from-amber-400/30 to-green-400/30 border border-amber-400/40 text-amber-200 hover:from-amber-400/50 hover:to-green-400/50'
                  : 'bg-amber-900/10 border border-amber-500/10 text-amber-500/20'
              }`}
              style={{
                fontFamily: '"Crimson Text", serif',
                boxShadow: input.trim() && !isTyping ? '0 2px 12px rgba(251, 191, 36, 0.2)' : 'none'
              }}
            >
              Send
            </button>
          </div>

          {/* Harmony/View Orb Button - More Prominent */}
          <div className="flex items-center justify-center mt-3">
            <button
              type="button"
              onClick={handleHarmonyMode}
              className="px-4 py-2 bg-amber-400/20 hover:bg-amber-400/40 border border-amber-400/40 rounded-full transition-all text-amber-200 text-sm flex items-center gap-2"
              style={{ fontFamily: '"Crimson Text", serif' }}
            >
              <BelongingHeartIcon size={16} color="#fbbf24" />
              <span>View Orb</span>
            </button>
          </div>
        </form>
      </div>

      {/* Footer - Gentle */}
      <div className="relative z-10 border-t border-amber-400/10 p-4 text-center bg-gradient-to-r from-amber-50/5 to-green-50/5 backdrop-blur-sm">
        <p className="text-amber-400/40 text-sm italic mb-2" style={{ fontFamily: '"Crimson Text", serif' }}>
          "We're just friends, writing letters on a summer afternoon."
        </p>
        <div className="flex items-center justify-center gap-2 text-amber-500/20 text-[10px] mb-2">
          <span>A shared space of</span>
          <a 
            href="https://belongingsessions.com" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-amber-400/40 hover:text-amber-400 underline transition-colors"
          >
            BelongingSessions.com
          </a>
          <span>•</span>
          <span>Created by</span>
          <a 
            href="https://planetaryfam.com" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-amber-400/40 hover:text-amber-400 underline transition-colors"
          >
            PlanetaryFam
          </a>
        </div>
        <div className="flex justify-center gap-3 items-center flex-wrap">
          <a
            href="https://elchme.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-amber-400/40 hover:text-amber-400 text-[10px] transition-colors flex items-center gap-1.5"
          >
            <span>Emergency Empathy?</span>
            <span className="font-semibold tracking-wide" style={{ fontFamily: 'monospace' }}>
              <span style={{ color: '#ef4444' }}>E</span>
              <span style={{ color: '#f97316' }}>l</span>
              <span style={{ color: '#eab308' }}>c</span>
              <span style={{ color: '#22c55e' }}>h</span>
              <span style={{ color: '#06b6d4' }}>m</span>
              <span style={{ color: '#8b5cf6' }}>E</span>
            </span>
          </a>
          <span className="text-amber-400/20">•</span>
          <button
            onClick={() => {
              sessionStorage.clear();
              window.location.reload();
            }}
            className="text-amber-400/30 hover:text-amber-400 text-[10px] transition-colors flex items-center gap-1"
            style={{ fontFamily: '"Crimson Text", serif' }}
          >
            <span>◉</span>
            <span>collective consciousness orb</span>
          </button>
          <span className="text-amber-400/20">•</span>
          <button
            onClick={() => window.location.href = '/?view=about'}
            className="text-amber-400/30 hover:text-amber-400 text-[10px] transition-colors underline"
            style={{ fontFamily: '"Crimson Text", serif' }}
          >
            about
          </button>
          <span className="text-amber-400/20">•</span>
          <button
            onClick={() => setShowEpicSummary(true)}
            className="text-amber-400/30 hover:text-amber-400 text-[10px] transition-colors flex items-center gap-1"
            style={{ fontFamily: '"Crimson Text", serif' }}
            data-testid="footer-summary-btn"
          >
            <span>✦</span>
            <span>session summary</span>
          </button>
        </div>
      </div>
      
      {/* Ecosystem Navigation - Mobile app style bottom nav */}
      <EcosystemNav variant="creekside" />
      
      {/* Contact Orb */}
      <ContactOrb variant="creekside" />
    </div>
  );
};

export default DefianceCreeksideInterface;
